import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Sidebar from './Sidebar'; 
import './ReportsPage.css';

export default function ReportsPage() {
  const { hash } = useParams();
  const navigate = useNavigate();
 
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    location: '',
    actionType: '',
    deploymentName: '',
    poFoc: '',
    includeFiles: false
  });
  const [reportData, setReportData] = useState([]);
  const [allReportData, setAllReportData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [deploymentNames, setDeploymentNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [generatingPhotoReport, setGeneratingPhotoReport] = useState(false);
  const [generatingMovementCompilation, setGeneratingMovementCompilation] = useState(false);

  const handleSidebarSelect = (section) => {
    navigate(`/a/${hash}`, { state: { section } });
  };

  // Fetch locations for dropdown
  const fetchLocations = async () => {
    try {
      const response = await fetch('/api/locations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("token")}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  // Fetch unique deployment names for dropdown
  const fetchDeploymentNames = async () => {
    try {
      const response = await fetch('/api/deployments/names', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("token")}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDeploymentNames(data);
      }
    } catch (err) {
      console.error("Error fetching deployment names:", err);
    }
  };

  const handleFetchReport = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/reports/all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(`Failed to fetch reports (${response.status})`);
      }

      const data = await response.json();
      console.log("Fetched report data:", data);
      
      // Ensure we have an array and convert counts to numbers
      const deployments = Array.isArray(data) ? data.map(row => ({
        ...row,
        deployed_count: Number(row.deployed_count) || 0,
        collected_count: Number(row.collected_count) || 0,
        transferred_count: Number(row.transferred_count) || 0,
        photo_count: Number(row.photo_count) || 0,
        po_count: Number(row.po_count) || 0,
        foc_count: Number(row.foc_count) || 0
      })) : [];
      
      setAllReportData(deployments);
      setReportData(deployments);
    } catch (err) {
      console.error("Error fetching reports:", err);
      alert("Error fetching data: " + err.message + "\n\nPlease check that the API endpoint exists.");
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to the data
  const applyFilters = () => {
    let filtered = [...allReportData];

    if (filters.startDate) {
      const startDateTime = filters.startTime 
        ? new Date(`${filters.startDate}T${filters.startTime}`)
        : new Date(`${filters.startDate}T00:00:00`);
      
      filtered = filtered.filter(row => {
        if (!row.created_at) return false;
        return new Date(row.created_at) >= startDateTime;
      });
    }

    if (filters.endDate) {
      const endDateTime = filters.endTime 
        ? new Date(`${filters.endDate}T${filters.endTime}`)
        : new Date(`${filters.endDate}T23:59:59`);
      
      filtered = filtered.filter(row => {
        if (!row.created_at) return false;
        return new Date(row.created_at) <= endDateTime;
      });
    }

    if (filters.location) {
      filtered = filtered.filter(row => 
        row.deployment_location_name === filters.location
      );
    }

    if (filters.actionType) {
      filtered = filtered.filter(row => 
        row.deployment_type === filters.actionType
      );
    }

    if (filters.deploymentName) {
      filtered = filtered.filter(row => 
        row.deployment_name === filters.deploymentName
      );
    }

    if (filters.poFoc) {
      filtered = filtered.filter(row => 
        filters.poFoc === 'PO' ? row.po_count > 0 : row.foc_count > 0
      );
    }

    if (filters.includeFiles) {
      filtered = filtered.filter(row => 
        row.has_ack_pdf || (row.photo_count && row.photo_count > 0)
      );
    }

    setReportData(filtered);
    setSelectedRows(new Set());
  };

  useEffect(() => {
    handleFetchReport();
    fetchLocations();
    fetchDeploymentNames();
  }, []);

  const handleRowSelect = (deploymentId) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(deploymentId)) {
      newSelected.delete(deploymentId);
    } else {
      newSelected.add(deploymentId);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === reportData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(reportData.map(row => row.deployment_id)));
    }
  };

  const formatStatus = (status) => {
    if (!status) return "-";
    return status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  // Shared helper: fetch full item-level details for the selected deployments.
  // Used by both the PDF and Excel export paths so they stay in sync.
  const fetchSelectedDeploymentsWithDetails = async () => {
    const selectedDeployments = reportData.filter(row => 
      selectedRows.has(row.deployment_id)
    );

    return Promise.all(
      selectedDeployments.map(async (deployment) => {
        try {
          const response = await fetch(`/api/deployments/${deployment.deployment_id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            return {
              ...deployment,
              details: data.deployment || {},
              items: data.items || []
            };
          }
          return { ...deployment, details: {}, items: [] };
        } catch (err) {
          console.error(`Error fetching details for deployment ${deployment.deployment_id}:`, err);
          return { ...deployment, details: {}, items: [] };
        }
      })
    );
  };

  // Generate comprehensive PDF with all deployment details
  const handleDownloadPDF = async () => {
    if (selectedRows.size === 0) {
      alert("Please select at least one deployment to generate PDF.");
      return;
    }

    setGeneratingPDF(true);
    try {
      const deploymentsWithDetails = await fetchSelectedDeploymentsWithDetails();

      const doc = new jsPDF();
      
      let yPos = 20;

      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text('Deployment Report', 105, yPos, { align: 'center' });
      yPos += 15;

      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated: ${new Date().toLocaleString()}`, 105, yPos, { align: 'center' });
      yPos += 8;
      doc.text(`Total Deployments: ${deploymentsWithDetails.length}`, 105, yPos, { align: 'center' });
      yPos += 20;

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Deployments Summary', 14, yPos);
      yPos += 10;

      const summaryData = deploymentsWithDetails.map((dep, index) => {
        const deployCount = dep.items.filter(it => it.action_type === 'DEPLOY').length;
        const collectCount = dep.items.filter(it => it.action_type === 'COLLECT').length;
        const transferCount = dep.items.filter(it => it.action_type === 'TRANSFER').length;
        const totalItems = deployCount + collectCount + transferCount;

        return [
          index + 1,
          dep.deployment_id,
          dep.created_at ? new Date(dep.created_at).toLocaleDateString() : 'N/A',
          dep.deployment_location_name || 'N/A',
          dep.deployment_type || 'N/A',
          formatStatus(dep.status),
          totalItems
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [['#', 'ID', 'Date', 'Location', 'Type', 'Status', 'Total Items']],
        body: summaryData,
        theme: 'striped',
        headStyles: { 
          fillColor: [76, 175, 80], 
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 },
        styles: { cellPadding: 3 }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      const totalDevices = deploymentsWithDetails.reduce((sum, dep) => 
        sum + dep.items.filter(it => it.action_type === 'DEPLOY').length, 0
      );
      const totalCollected = deploymentsWithDetails.reduce((sum, dep) => 
        sum + dep.items.filter(it => it.action_type === 'COLLECT').length, 0
      );
      const totalTransferred = deploymentsWithDetails.reduce((sum, dep) => 
        sum + dep.items.filter(it => it.action_type === 'TRANSFER').length, 0
      );

      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Overall Statistics', 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`• Total Devices Deployed: ${totalDevices}`, 14, yPos);
      yPos += 6;
      doc.text(`• Total Devices Collected: ${totalCollected}`, 14, yPos);
      yPos += 6;
      doc.text(`• Total Devices Transferred: ${totalTransferred}`, 14, yPos);
      yPos += 6;
      doc.text(`• Grand Total Devices: ${totalDevices + totalCollected + totalTransferred}`, 14, yPos);

      deploymentsWithDetails.forEach((dep, index) => {
        doc.addPage();
        
        let yPos = 20;

        doc.setFillColor(76, 175, 80);
        doc.rect(14, yPos - 5, 182, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`Deployment #${index + 1} - ID: ${dep.deployment_id}`, 16, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        
        doc.text('Date & Time:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(dep.created_at ? new Date(dep.created_at).toLocaleString() : 'N/A', 50, yPos);
        yPos += 5;

        doc.setFont(undefined, 'bold');
        doc.text('Location:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(dep.deployment_location_name || 'N/A', 50, yPos);
        yPos += 5;

        doc.setFont(undefined, 'bold');
        doc.text('Type:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(dep.deployment_type || 'N/A', 50, yPos);
        yPos += 5;

        doc.setFont(undefined, 'bold');
        doc.text('Status:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(formatStatus(dep.status) || 'N/A', 50, yPos);
        yPos += 5;

        doc.setFont(undefined, 'bold');
        doc.text('Vendor:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(dep.details.vendor_name || 'N/A', 50, yPos);
        yPos += 5;

        doc.setFont(undefined, 'bold');
        doc.text('Created By:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(dep.details.created_by_name || 'N/A', 50, yPos);
        yPos += 10;

        const deployItems = dep.items.filter(it => it.action_type === 'DEPLOY');
        const collectItems = dep.items.filter(it => it.action_type === 'COLLECT');
        const transferItems = dep.items.filter(it => it.action_type === 'TRANSFER');

        if (deployItems.length > 0) {
          doc.setFont(undefined, 'bold');
          doc.setFontSize(10);
          doc.text(`Devices Deployed (${deployItems.length})`, 14, yPos);
          yPos += 5;

          autoTable(doc, {
            startY: yPos,
            head: [['#', 'Model', 'Colour', 'IMEI', 'Serial Number', 'Status']],
            body: deployItems.map((item, idx) => [
              idx + 1,
              item.asset1_name || '-',
              item.asset2_name || '-',
              item.imei_number || '-',
              item.serial_number || '-',
              item.status || 'PENDING'
            ]),
            theme: 'grid',
            headStyles: { fillColor: [76, 175, 80], fontSize: 8 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 14, right: 14 },
            styles: { cellPadding: 2 }
          });
          yPos = doc.lastAutoTable.finalY + 8;
        }

        if (collectItems.length > 0) {
          doc.setFont(undefined, 'bold');
          doc.setFontSize(10);
          doc.text(`Devices Collected (${collectItems.length})`, 14, yPos);
          yPos += 5;

          autoTable(doc, {
            startY: yPos,
            head: [['#', 'Model', 'Colour', 'IMEI', 'Serial Number', 'Status']],
            body: collectItems.map((item, idx) => [
              idx + 1,
              item.asset1_name || '-',
              item.asset2_name || '-',
              item.imei_number || '-',
              item.serial_number || '-',
              item.status || 'PENDING'
            ]),
            theme: 'grid',
            headStyles: { fillColor: [255, 193, 7], fontSize: 8 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 14, right: 14 },
            styles: { cellPadding: 2 }
          });
          yPos = doc.lastAutoTable.finalY + 8;
        }

        if (transferItems.length > 0) {
          doc.setFont(undefined, 'bold');
          doc.setFontSize(10);
          doc.text(`Devices Transferred (${transferItems.length})`, 14, yPos);
          yPos += 5;

          autoTable(doc, {
            startY: yPos,
            head: [['#', 'Model', 'Colour', 'IMEI', 'Serial Number', 'Status']],
            body: transferItems.map((item, idx) => [
              idx + 1,
              item.asset1_name || '-',
              item.asset2_name || '-',
              item.imei_number || '-',
              item.serial_number || '-',
              item.status || 'PENDING'
            ]),
            theme: 'grid',
            headStyles: { fillColor: [33, 150, 243], fontSize: 8 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 14, right: 14 },
            styles: { cellPadding: 2 }
          });
          yPos = doc.lastAutoTable.finalY + 8;
        }
      });

      doc.save(`deployment-report-${new Date().getTime()}.pdf`);
      alert(`PDF report generated successfully with ${deploymentsWithDetails.length} deployments!`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Generate comprehensive Excel workbook with a Summary sheet and a Device Details sheet
  const handleDownloadExcel = async () => {
    if (selectedRows.size === 0) {
      alert("Please select at least one deployment to generate Excel report.");
      return;
    }

    setGeneratingExcel(true);
    try {
      const deploymentsWithDetails = await fetchSelectedDeploymentsWithDetails();

      const summaryRows = deploymentsWithDetails.map((dep, index) => {
        const deployCount = dep.items.filter(it => it.action_type === 'DEPLOY').length;
        const collectCount = dep.items.filter(it => it.action_type === 'COLLECT').length;
        const transferCount = dep.items.filter(it => it.action_type === 'TRANSFER').length;

        return {
          '#': index + 1,
          'Deployment ID': dep.deployment_id,
          'Date': dep.created_at ? new Date(dep.created_at).toLocaleString() : 'N/A',
          'Location': dep.deployment_location_name || 'N/A',
          'Type': dep.deployment_type || 'N/A',
          'Vendor': dep.details.vendor_name || 'N/A',
          'Status': formatStatus(dep.status),
          'Deployed': deployCount,
          'Collected': collectCount,
          'Transferred': transferCount,
          'PO': dep.po_count || 0,
          'FOC': dep.foc_count || 0,
          'Total Items': deployCount + collectCount + transferCount
        };
      });

      const detailRows = [];
      deploymentsWithDetails.forEach(dep => {
        dep.items.forEach(item => {
          detailRows.push({
            'Deployment ID': dep.deployment_id,
            'Location': dep.deployment_location_name || 'N/A',
            'Action Type': item.action_type,
            'Model': item.asset1_name || '-',
            'Colour': item.asset2_name || '-',
            'IMEI': item.imei_number || '-',
            'Serial Number': item.serial_number || '-',
            'Status': item.status || 'PENDING'
          });
        });
      });

      const wb = XLSX.utils.book_new();

      const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
      summarySheet['!cols'] = Object.keys(summaryRows[0] || {}).map((key) => ({
        wch: Math.max(
          key.length,
          summaryRows.reduce((m, r) => Math.max(m, String(r[key] ?? '').length), 0)
        ) + 2
      }));
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

      if (detailRows.length) {
        const detailSheet = XLSX.utils.json_to_sheet(detailRows);
        detailSheet['!cols'] = Object.keys(detailRows[0] || {}).map((key) => ({
          wch: Math.max(
            key.length,
            detailRows.reduce((m, r) => Math.max(m, String(r[key] ?? '').length), 0)
          ) + 2
        }));
        XLSX.utils.book_append_sheet(wb, detailSheet, 'Device Details');
      }

      XLSX.writeFile(wb, `deployment-report-${new Date().getTime()}.xlsx`);
      alert(`Excel report generated successfully with ${deploymentsWithDetails.length} deployments!`);
    } catch (error) {
      console.error("Error generating Excel report:", error);
      alert("Error generating Excel report. Please try again.");
    } finally {
      setGeneratingExcel(false);
    }
  };

  // Generate Photo Report PDF
  const handleDownloadPhotoReport = async () => {
    if (selectedRows.size === 0) {
      alert("Please select at least one deployment to generate photo report.");
      return;
    }

    setGeneratingPhotoReport(true);
    try {
      const selectedDeployments = reportData.filter(row => 
        selectedRows.has(row.deployment_id)
      );

      const deploymentsWithPhotos = await Promise.all(
        selectedDeployments.map(async (deployment) => {
          try {
            const response = await fetch(`/api/deployments/${deployment.deployment_id}/files`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              const photos = Array.isArray(data.files) 
                ? data.files.filter(f => f.file_type === 'PHOTO')
                : [];
              return {
                ...deployment,
                photos: photos
              };
            }
            return { ...deployment, photos: [] };
          } catch (err) {
            console.error(`Error fetching photos for deployment ${deployment.deployment_id}:`, err);
            return { ...deployment, photos: [] };
          }
        })
      );

      const deploymentsWithPhotoData = deploymentsWithPhotos.filter(d => d.photos.length > 0);

      if (deploymentsWithPhotoData.length === 0) {
        alert("No photos found in the selected deployments.");
        setGeneratingPhotoReport(false);
        return;
      }

      const doc = new jsPDF();
      
      let yPos = 20;
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text('Photo Report', 105, yPos, { align: 'center' });
      yPos += 15;

      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated: ${new Date().toLocaleString()}`, 105, yPos, { align: 'center' });
      yPos += 8;
      doc.text(`Deployments with Photos: ${deploymentsWithPhotoData.length}`, 105, yPos, { align: 'center' });
      yPos += 8;
      
      const totalPhotos = deploymentsWithPhotoData.reduce((sum, d) => sum + d.photos.length, 0);
      doc.text(`Total Photos: ${totalPhotos}`, 105, yPos, { align: 'center' });
      yPos += 20;

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Deployments Summary', 14, yPos);
      yPos += 10;

      const summaryData = deploymentsWithPhotoData.map((dep, index) => [
        index + 1,
        dep.deployment_id,
        dep.deployment_location_name || 'N/A',
        dep.photos.length + ' photos'
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['#', 'ID', 'Location', 'Photos']],
        body: summaryData,
        theme: 'striped',
        headStyles: { 
          fillColor: [33, 150, 243], 
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: { fontSize: 9 },
        margin: { left: 14, right: 14 }
      });

      for (let i = 0; i < deploymentsWithPhotoData.length; i++) {
        const dep = deploymentsWithPhotoData[i];
        doc.addPage();
        yPos = 20;

        doc.setFillColor(33, 150, 243);
        doc.rect(14, yPos - 5, 182, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`Deployment #${i + 1} - ID: ${dep.deployment_id}`, 16, yPos);
        doc.setTextColor(0, 0, 0);
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('Location:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(dep.deployment_location_name || 'N/A', 50, yPos);
        yPos += 5;

        doc.setFont(undefined, 'bold');
        doc.text('Date:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(dep.created_at ? new Date(dep.created_at).toLocaleString() : 'N/A', 50, yPos);
        yPos += 5;

        doc.setFont(undefined, 'bold');
        doc.text('Photos:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(`${dep.photos.length} photo(s)`, 50, yPos);
        yPos += 15;

        for (let j = 0; j < dep.photos.length; j++) {
          const photo = dep.photos[j];
          
          try {
            const imgResponse = await fetch(`/api/deployment-files/${photo.file_id}/download`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
              }
            });

            if (imgResponse.ok) {
              const blob = await imgResponse.blob();
              const reader = new FileReader();
              
              await new Promise((resolve, reject) => {
                reader.onload = () => {
                  try {
                    const imgData = reader.result;
                    
                    if (yPos > 200) {
                      doc.addPage();
                      yPos = 20;
                    }

                    doc.setFontSize(9);
                    doc.setFont(undefined, 'bold');
                    doc.text(`Photo ${j + 1}:`, 14, yPos);
                    doc.setFont(undefined, 'normal');
                    doc.text(photo.original_name || `image_${j + 1}`, 35, yPos);
                    yPos += 5;

                    const maxWidth = 180;
                    const maxHeight = 120;
                    doc.addImage(imgData, 'JPEG', 14, yPos, maxWidth, maxHeight);
                    yPos += maxHeight + 10;

                    resolve();
                  } catch (err) {
                    console.error('Error adding image to PDF:', err);
                    resolve();
                  }
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            }
          } catch (err) {
            console.error(`Error loading photo ${photo.file_id}:`, err);
            doc.setFontSize(9);
            doc.text(`[Photo ${j + 1} - Failed to load]`, 14, yPos);
            yPos += 10;
          }
        }
      }

      doc.save(`photo-report-${new Date().getTime()}.pdf`);
      alert(`Photo report generated successfully with ${totalPhotos} photos from ${deploymentsWithPhotoData.length} deployments!`);
    } catch (error) {
      console.error("Error generating photo report:", error);
      alert("Error generating photo report. Please try again.");
    } finally {
      setGeneratingPhotoReport(false);
    }
  };

  // Generate Device Movement Compilation PDF
  const handleDownloadMovementCompilation = async () => {
    if (selectedRows.size === 0) {
      alert("Please select at least one deployment to generate movement compilation.");
      return;
    }

    setGeneratingMovementCompilation(true);
    try {
      const selectedDeployments = reportData.filter(row => 
        selectedRows.has(row.deployment_id)
      );

      const deploymentsWithPDFs = await Promise.all(
        selectedDeployments.map(async (deployment) => {
          try {
            const response = await fetch(`/api/deployments/${deployment.deployment_id}/files`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              // Only the current (newest) acknowledgement PDF; older deployments may
              // still have superseded copies from before re-uploads replaced them
              const pdfs = Array.isArray(data.files)
                ? data.files.filter(f => f.file_type === 'ACK_PDF').slice(-1)
                : [];
              return {
                ...deployment,
                pdfs: pdfs
              };
            }
            return { ...deployment, pdfs: [] };
          } catch (err) {
            console.error(`Error fetching PDFs for deployment ${deployment.deployment_id}:`, err);
            return { ...deployment, pdfs: [] };
          }
        })
      );

      const deploymentsWithPDFData = deploymentsWithPDFs.filter(d => d.pdfs.length > 0);

      if (deploymentsWithPDFData.length === 0) {
        alert("No PDF files found in the selected deployments.");
        setGeneratingMovementCompilation(false);
        return;
      }

      // Import PDFLib dynamically
      const { PDFDocument } = await import('pdf-lib');
      const mergedPdf = await PDFDocument.create();

      let totalPages = 0;

      for (let i = 0; i < deploymentsWithPDFData.length; i++) {
        const dep = deploymentsWithPDFData[i];
        
        for (let j = 0; j < dep.pdfs.length; j++) {
          const pdfFile = dep.pdfs[j];
          
          try {
            const pdfResponse = await fetch(`/api/deployment-files/${pdfFile.file_id}/download`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
              }
            });

            if (pdfResponse.ok) {
              const pdfBytes = await pdfResponse.arrayBuffer();
              const pdfDoc = await PDFDocument.load(pdfBytes);
              const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
              
              copiedPages.forEach((page) => {
                mergedPdf.addPage(page);
                totalPages++;
              });
            }
          } catch (err) {
            console.error(`Error loading PDF ${pdfFile.file_id}:`, err);
          }
        }
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `device-movement-compilation-${new Date().getTime()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      const totalPDFs = deploymentsWithPDFData.reduce((sum, d) => sum + d.pdfs.length, 0);
      alert(`Movement compilation generated successfully!\n\n${totalPDFs} PDFs compiled from ${deploymentsWithPDFData.length} deployments.\nTotal pages: ${totalPages}`);
    } catch (error) {
      console.error("Error generating movement compilation:", error);
      alert("Error generating movement compilation. Please try again.");
    } finally {
      setGeneratingMovementCompilation(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      location: '',
      actionType: '',
      deploymentName: '',
      poFoc: '',
      includeFiles: false
    });
    setReportData(allReportData);
    setSelectedRows(new Set());
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar hash={hash} onSelect={handleSidebarSelect} />

      <div style={{ display: 'flex', flex: 1, gap: '20px', padding: '20px' }}>
        <div style={{
          width: '280px',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          height: 'fit-content',
          position: 'sticky',
          top: '20px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>Filters</h3>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>Start Date</label>
            <input 
              type="date" 
              value={filters.startDate} 
              onChange={(e) => setFilters({...filters, startDate: e.target.value})} 
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>Start Time</label>
            <input 
              type="time" 
              value={filters.startTime} 
              onChange={(e) => setFilters({...filters, startTime: e.target.value})} 
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>End Date</label>
            <input 
              type="date" 
              value={filters.endDate} 
              onChange={(e) => setFilters({...filters, endDate: e.target.value})} 
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>End Time</label>
            <input 
              type="time" 
              value={filters.endTime} 
              onChange={(e) => setFilters({...filters, endTime: e.target.value})} 
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>Location</label>
            <select 
              value={filters.location} 
              onChange={(e) => setFilters({...filters, location: e.target.value})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.location_id} value={loc.location_name}>
                  {loc.location_name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>Action Type</label>
            <select 
              value={filters.actionType} 
              onChange={(e) => setFilters({...filters, actionType: e.target.value})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All Action Types</option>
              <option value="DEPLOYMENT">Deployment</option>
              <option value="COLLECTION">Collection</option>
              <option value="TRANSFER">Transfer</option>
              <option value="MIXED">Mixed</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>Type (Deployment Name)</label>
            <select 
              value={filters.deploymentName} 
              onChange={(e) => setFilters({...filters, deploymentName: e.target.value})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All Types</option>
              {deploymentNames.map((name, index) => (
                <option key={index} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>PO/FOC</label>
            <select 
              value={filters.poFoc} 
              onChange={(e) => setFilters({...filters, poFoc: e.target.value})}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">All</option>
              <option value="PO">PO Devices</option>
              <option value="FOC">FOC Devices</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input 
                type="checkbox" 
                checked={filters.includeFiles} 
                onChange={(e) => setFilters({...filters, includeFiles: e.target.checked})} 
                style={{ marginRight: '8px' }}
              />
              Only with Attachments
            </label>
          </div>

          <button 
            className="add-user-button" 
            onClick={applyFilters}
            style={{ width: '100%', marginBottom: '10px' }}
          >
            🔍 Apply Filters
          </button>
          
          <button 
            className="add-user-button" 
            onClick={handleClearFilters}
            style={{ width: '100%', backgroundColor: '#6c757d' }}
          >
            Clear Filters
          </button>
        </div>

        <div className="user-list-container" style={{ flex: 1, margin: 0 }}>
          <div className="search-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' }}>
            <div>
              <h2 style={{ margin: 0 }}>System Reports</h2>
              <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Showing {reportData.length} deployments | {selectedRows.size} selected
              </p>
            </div>
            <div className="button-container" style={{ margin: 0, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                className="add-user-button" 
                onClick={handleSelectAll}
                style={{ backgroundColor: '#17a2b8' }}
              >
                {selectedRows.size === reportData.length ? '☑️ Deselect All' : '☐ Select All'}
              </button>
              <button 
                className="add-user-button" 
                onClick={handleDownloadMovementCompilation} 
                disabled={selectedRows.size === 0 || generatingMovementCompilation}
                style={{ 
                  opacity: selectedRows.size === 0 ? 0.5 : 1, 
                  cursor: selectedRows.size === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor: '#9C27B0'
                }}
              >
                {generatingMovementCompilation ? '⏳ Generating...' : '📋 Device Movement Compilation'}
              </button>
              <button 
                className="add-user-button" 
                onClick={handleDownloadPhotoReport} 
                disabled={selectedRows.size === 0 || generatingPhotoReport}
                style={{ 
                  opacity: selectedRows.size === 0 ? 0.5 : 1, 
                  cursor: selectedRows.size === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor: '#2196F3'
                }}
              >
                {generatingPhotoReport ? '⏳ Generating...' : '📷 Photo Report'}
              </button>
              <button 
                className="add-user-button" 
                onClick={handleDownloadExcel} 
                disabled={selectedRows.size === 0 || generatingExcel}
                style={{ 
                  opacity: selectedRows.size === 0 ? 0.5 : 1, 
                  cursor: selectedRows.size === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor: '#217346'
                }}
              >
                {generatingExcel ? '⏳ Generating...' : '📊 Excel Summary Report'}
              </button>
              <button 
                className="add-user-button" 
                onClick={handleDownloadPDF} 
                disabled={selectedRows.size === 0 || generatingPDF}
                style={{ 
                  opacity: selectedRows.size === 0 ? 0.5 : 1, 
                  cursor: selectedRows.size === 0 ? 'not-allowed' : 'pointer', 
                  backgroundColor: '#ff000d',
                }}
              >
                {generatingPDF ? '⏳ Generating...' : '📄 PDF Summary Report'}
              </button>
            </div>
          </div>

          <div className="UserList-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>
                    <input 
                      type="checkbox"
                      checked={selectedRows.size === reportData.length && reportData.length > 0}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ width: '180px', minWidth: '180px' }}>Date & Time</th>
                  <th style={{ width: '180px', minWidth: '180px' }}>Location</th>
                  <th>Type</th>
                  <th>Vendor</th>
                  <th>Status</th>
                  <th>Deployed</th>
                  <th>Collected</th>
                  <th>Transferred</th>
                  <th>Files</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData) && reportData.length > 0 ? (
                  reportData.map((row) => (
                    <tr 
                      key={row.deployment_id}
                      style={{ 
                        backgroundColor: selectedRows.has(row.deployment_id) ? '#e3f2fd' : 'transparent' 
                      }}
                    >
                      <td style={{ textAlign: 'center' }}>
                        <input 
                          type="checkbox"
                          checked={selectedRows.has(row.deployment_id)}
                          onChange={() => handleRowSelect(row.deployment_id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {row.created_at
                           ? new Date(row.created_at).toLocaleString()
                           : "-"}
                      </td>
                      <td style={{ whiteSpace: 'wrap' }}><strong>{row.deployment_location_name || "N/A"}</strong></td>
                       <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{row.deployment_name || "N/A"}</td>
                      <td>{row.vendor_name || "N/A"}</td>
                      <td>
                        <span className={`status-badge ${(row.status || 'pending').toLowerCase()}`}>
                          {formatStatus(row.status)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ color: '#48bb78', fontWeight: 'bold' }}>
                          {row.deployed_count || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ color: '#ed8936', fontWeight: 'bold' }}>
                          {row.collected_count || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ color: '#4299e1', fontWeight: 'bold' }}>
                          {row.transferred_count || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'left', padding: '8px' }}>
                        {row.has_ack_pdf || row.photo_count > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {row.has_ack_pdf && (
                              <span style={{ fontSize: '13px', color: '#2563eb' }}>PDF</span>
                            )}
                            {row.photo_count > 0 && (
                              <span style={{ fontSize: '13px', color: '#059669' }}>
                                {row.photo_count} {row.photo_count === 1 ? 'Photo' : 'Photos'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#cbd5e0' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      {loading ? "Loading deployments..." : "No deployment data found matching your filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}